/**
 * NoteFilterFields
 *
 * Controlled note filter inputs shared by the owner form (frozen base filters,
 * search hidden) and the public viewer narrowing panel (search shown).
 * Colors render as toggleable swatches mirroring the app's note filters.
 */
import { memo } from 'react';
import { Input, Stack, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { ColorSwatchToggleGroup } from '@/components/common/ColorSwatchToggleGroup';
import { ICON_SIZE_SM } from '@/constants/ui';
import type { ShareViewerFilters } from '@/types/share';

import { SHARE_NOTE_COLOR_VALUES } from '../shareConstants';
import { restrictValues } from './shareFilterOptions';
import { ShareFilterDateRange } from './ShareFilterDateRange';

interface NoteFilterFieldsProps {
  value: ShareViewerFilters;
  onPatch: (patch: Partial<ShareViewerFilters>) => void;
  showSearch?: boolean;
  /** Owner-shared color subset; empty / missing shows all colors. */
  allowedColors?: string[];
}

export const NoteFilterFields = memo(function NoteFilterFields({
  value,
  onPatch,
  showSearch = false,
  allowedColors,
}: NoteFilterFieldsProps) {
  const { t } = useTranslation();
  const selectedColors = value.noteColors ?? [];
  const colorValues = restrictValues(SHARE_NOTE_COLOR_VALUES, allowedColors);

  function toggleColor(color: string): void {
    const next = selectedColors.includes(color)
      ? selectedColors.filter((current) => current !== color)
      : [...selectedColors, color];
    onPatch({ noteColors: next });
  }

  return (
    <Stack gap="xs">
      <Input.Wrapper label={t('share.filters.colors')}>
        <ColorSwatchToggleGroup
          colors={colorValues}
          selected={selectedColors}
          onToggle={toggleColor}
          withLabelOffset
        />
      </Input.Wrapper>
      <ShareFilterDateRange
        fromLabel={t('share.filters.dateFrom')}
        toLabel={t('share.filters.dateTo')}
        fromValue={value.noteDateFrom}
        toValue={value.noteDateTo}
        onFromChange={(iso) => onPatch({ noteDateFrom: iso })}
        onToChange={(iso) => onPatch({ noteDateTo: iso })}
      />
      {showSearch && (
        <TextInput
          label={t('share.filters.search')}
          leftSection={<IconSearch size={ICON_SIZE_SM} />}
          value={value.noteSearch ?? ''}
          onChange={(event) =>
            onPatch({ noteSearch: event.currentTarget.value })
          }
        />
      )}
    </Stack>
  );
});
